import styled from 'styled-components';
import { IconButton, useThemeColor, Link, Text, Icon } from '@wistia/vhs';
import type { JSX } from 'react';
import { useState, useRef, useEffect } from 'react';
import { isNotNil } from '~/utilities/type-guards';

const TitleInput = styled.input`
  border: none;
  outline: none;
  width: 100%;
`;

const ClickToEdit = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.spacing.space02};
`;

const ShowButtonOnHover = styled(IconButton)`
  opacity: 0;

  ${ClickToEdit}:hover & {
    opacity: 1;
  }
`;

export const uploadTitleKey = (id: string): string => `UPLOAD_TITLE_${id}`;

export const EditableUploadName = ({
  id,
  name,
  href = null,
  disableEditing = false,
  boldText = false,
  backgroundColor = 'none',
  largeText = false,
}: {
  id: string;
  name: string;
  href?: string | null;
  disableEditing?: boolean;
  boldText?: boolean;
  backgroundColor?: string;
  largeText?: boolean;
}): JSX.Element => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const editorRef = useRef<HTMLInputElement>(null);
  const brandBlue500 = useThemeColor('brandBlue500');

  const displayName = localStorage.getItem(uploadTitleKey(id)) ?? name;
  const [uploadTitle, setUploadTitle] = useState<string>(displayName);

  useEffect(() => {
    if (isEditing) {
      editorRef.current?.focus();
      editorRef.current?.select();
    } else {
      localStorage.setItem(uploadTitleKey(id), uploadTitle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  useEffect(() => {
    setUploadTitle(displayName);
  }, [displayName]);

  if (isNotNil(href)) {
    return (
      <Link href={href}>
        <Text
          variant={largeText ? 'subtitle1' : 'subtitle2'}
          bold={boldText}
          colorOverride={brandBlue500}
        >
          {uploadTitle}
        </Text>
      </Link>
    );
  }

  return isEditing ? (
    <Text
      style={{ width: '100%' }}
      variant={largeText ? 'subtitle1' : 'subtitle2'}
    >
      <TitleInput
        ref={editorRef}
        value={uploadTitle}
        style={{ backgroundColor }}
        onChange={(event) => setUploadTitle(event.target.value)}
        onBlur={() => setIsEditing(false)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            setIsEditing(false);
          }
        }}
      />
    </Text>
  ) : (
    <ClickToEdit>
      <Text
        variant={largeText ? 'subtitle1' : 'subtitle2'}
        bold={boldText}
      >
        {displayName}
      </Text>
      {disableEditing ? null : (
        <ShowButtonOnHover
          aria-label="edit title"
          preventTooltip={true}
          size="sm"
          icon={
            <Icon
              type="pencil"
              size="sm"
            />
          }
          onClick={() => setIsEditing(true)}
        />
      )}
    </ClickToEdit>
  );
};
